import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './msal.config';

/**
 * MSAL Instance
 * This is a singleton that should be created once and reused throughout the app
 */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialize MSAL instance
 * This must be called before using the instance
 */
let initializationPromise: Promise<void> | null = null;

export function initializeMsal() {
  if (!initializationPromise) {
    initializationPromise = msalInstance.initialize().then(() => {
      // Handle redirect promise for redirect flow
      return msalInstance
        .handleRedirectPromise()
        .then(async response => {
          if (response) {
            // User signed in successfully via redirect
            // Exchange Microsoft ID token for MPN access token
            // This must happen here because MSAL navigates away from /auth/callback
            // before AuthCallbackPage can run
            if (response.idToken && response.account) {
              try {
                // Dynamic import to avoid circular dependencies
                const { authService } = await import(
                  '@/services/auth/authService'
                );
                const { useAuthStore, useSessionStore } = await import(
                  '@/stores'
                );

                const mpnAuthResponse =
                  await authService.exchangeMicrosoftToken(response.idToken);

                // Clear session expired flag on successful re-auth
                useSessionStore.getState().setSessionExpired(false);

                // Store MPN tokens
                const { setMpnTokens, setMsalAuthentication } =
                  useAuthStore.getState();
                setMsalAuthentication(response.account);
                setMpnTokens(
                  mpnAuthResponse.accessToken,
                  mpnAuthResponse.refreshToken
                );

                // Navigate to clients after successful token exchange
                window.location.href = '/clients';
              } catch (error) {
                console.error('Token exchange failed:', error);
                // On error, redirect to login
                window.location.href = '/login';
              }
            }
          }
        })
        .catch(error => {
          console.error('MSAL: Error handling redirect promise', error);
        });
    });
  }
  return initializationPromise;
}
